import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Check, AlertTriangle, X, RefreshCw, FileText, CreditCard, Shield, Minus, Plus, Flag, Download, Lock } from "lucide-react";

const MatchupManager = ({ scheduleId, onClose }) => {
  const { api, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [homeScore, setHomeScore] = useState("");
  const [awayScore, setAwayScore] = useState("");
  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState(null);

  const loadData = useCallback(() => {
    setLoading(true);
    api().get(`/matchup/${scheduleId}`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [api, scheduleId]);

  useEffect(() => { loadData(); }, [loadData]);

  const isCompleted = data?.matchup?.status === "completed";

  const verifyPlayer = async (playerId, status) => {
    if (isCompleted) return;
    // Optimistic update — keeps scroll position (full re-fetch was scrolling the
    // user back to the top of the page on every click).
    setData(d => {
      if (!d) return d;
      const inHome = (d.home_players || []).some(p => p.id === playerId);
      const key = inHome ? "home_verifications" : "away_verifications";
      return { ...d, matchup: { ...d.matchup, [key]: { ...(d.matchup[key] || {}), [playerId]: status } } };
    });
    try {
      await api().post(`/matchup/${scheduleId}/verify`, { player_id: playerId, status });
    } catch {
      loadData(); // rollback by re-fetching only on failure
    }
  };

  const updatePlayCount = async (playerId, count) => {
    if (isCompleted) return;
    const safeCount = Math.max(0, count);
    setData(d => {
      if (!d) return d;
      const inHome = (d.home_players || []).some(p => p.id === playerId);
      const key = inHome ? "home_play_counts" : "away_play_counts";
      return { ...d, matchup: { ...d.matchup, [key]: { ...(d.matchup[key] || {}), [playerId]: safeCount } } };
    });
    try {
      await api().post(`/matchup/${scheduleId}/playcount`, { player_id: playerId, play_count: safeCount });
    } catch {
      loadData();
    }
  };

  const requestAVAccess = async (side) => {
    if (isCompleted) return;
    await api().post(`/matchup/${scheduleId}/request-av`, { side });
    loadData();
  };

  const respondAVRequest = async (side, action) => {
    if (isCompleted) return;
    await api().post(`/matchup/${scheduleId}/respond-av`, { side, action });
    loadData();
  };

  const completeGame = async () => {
    setCompleteError(null);
    const h = parseInt(homeScore, 10);
    const a = parseInt(awayScore, 10);
    if (isNaN(h) || isNaN(a) || h < 0 || a < 0) {
      setCompleteError("Enter valid scores (0 or greater) for both teams");
      return;
    }
    setCompleting(true);
    try {
      await api().post(`/matchup/${scheduleId}/complete`, { home_score: h, away_score: a });
      setShowCompleteModal(false);
      setHomeScore(""); setAwayScore("");
      loadData();
    } catch (err) {
      setCompleteError(err.response?.data?.detail || "Failed to complete game");
    } finally {
      setCompleting(false);
    }
  };

  const downloadGamePDF = async () => {
    try {
      const r = await api().get(`/matchup/${scheduleId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Game_Report_${data?.schedule?.home_team_name || 'home'}_vs_${data?.schedule?.away_team_name || 'away'}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      alert('Failed to download game PDF');
    }
  };

  if (loading || !data) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading matchup...</div>;

  const { schedule, matchup, home_players, away_players, user_side } = data;
  const avRequests = matchup.av_access_requests || {};
  const isAdmin = user?.role === "ysv_admin" || user?.role === "league_admin";
  const myTeamSide = isAdmin ? null : user_side;
  const myPlayers = myTeamSide === "home" ? home_players : myTeamSide === "away" ? away_players : null;
  const oppPlayers = myTeamSide === "home" ? away_players : myTeamSide === "away" ? home_players : null;
  const oppSide = myTeamSide === "home" ? "away" : "home";
  const myTeamName = myTeamSide === "home" ? schedule.home_team_name : schedule.away_team_name;
  const oppTeamName = myTeamSide === "home" ? schedule.away_team_name : schedule.home_team_name;
  const oppRequestedMyAV = avRequests[oppSide] === "pending";
  const myAVAccessToOpp = avRequests[myTeamSide];
  const oppAVAccessGranted = myAVAccessToOpp === "approved";

  // Home team (or admin) can complete the game
  const canComplete = !isCompleted && (isAdmin || user_side === "home");
  const homeScoreFinal = matchup.home_score ?? 0;
  const awayScoreFinal = matchup.away_score ?? 0;

  return (
    <div data-testid="matchup-manager">
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 16, fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>
        <ArrowLeft size={14} /> Back to Schedule
      </button>

      {/* Title row with score if completed */}
      <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 700, marginBottom: 4 }}>
        {isCompleted ? (
          <span>
            {schedule.home_team_name} <span style={{ color: 'var(--cyan)' }}>({homeScoreFinal})</span>
            <span style={{ color: '#888', margin: '0 10px' }}>vs</span>
            {schedule.away_team_name} <span style={{ color: 'var(--cyan)' }}>({awayScoreFinal})</span>
          </span>
        ) : (
          <span>{schedule.home_team_name} vs {schedule.away_team_name}</span>
        )}
      </h2>
      <p style={{ color: '#888', fontSize: 13, marginBottom: 16 }}>{schedule.game_date} {schedule.game_time} &bull; {schedule.location} &bull; {schedule.age_group}</p>

      {isCompleted && (
        <div data-testid="game-completed-banner" style={{
          background: 'rgba(76,175,80,0.1)', border: '1px solid rgba(76,175,80,0.3)',
          borderRadius: 10, padding: '10px 14px', marginBottom: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Lock size={16} style={{ color: '#4caf50' }} />
            <span style={{ fontSize: 13, color: '#4caf50', fontWeight: 600 }}>Game Complete — Read-only</span>
          </div>
          <button data-testid="download-game-pdf" onClick={downloadGamePDF} style={{
            display: 'flex', alignItems: 'center', gap: 6, background: 'var(--cyan)', border: 'none',
            borderRadius: 8, color: '#000', padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}>
            <Download size={14} /> Download Game PDF
          </button>
        </div>
      )}

      {/* AV Request Banner */}
      {!isAdmin && !isCompleted && oppRequestedMyAV && (
        <div data-testid="av-request-banner" style={{
          background: 'rgba(255,152,0,0.1)', border: '2px solid rgba(255,152,0,0.4)',
          borderRadius: 12, padding: '16px 20px', marginBottom: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Shield size={22} style={{ color: '#ff9800' }} />
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: '#ff9800' }}>{oppTeamName} requested AV access</p>
              <p style={{ fontSize: 12, color: '#cc8a00' }}>They want to view your team's verification documents</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button data-testid="approve-av-request" onClick={() => respondAVRequest(oppSide, "approve")} style={{ background: '#4caf50', border: 'none', borderRadius: 8, color: '#fff', padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Approve</button>
            <button data-testid="deny-av-request" onClick={() => respondAVRequest(oppSide, "deny")} style={{ background: '#f44336', border: 'none', borderRadius: 8, color: '#fff', padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Deny</button>
          </div>
        </div>
      )}

      {/* Org Admin view */}
      {!isAdmin && myTeamSide && (
        <>
          <TeamHeader name={myTeamName} isMyTeam score={isCompleted ? (myTeamSide === "home" ? homeScoreFinal : awayScoreFinal) : null} onRefresh={loadData} />
          {myPlayers?.map(p => <OwnTeamCard key={p.id} player={p} playCounts={myTeamSide === "home" ? matchup.home_play_counts : matchup.away_play_counts} />)}

          {/* AV Access Request (hidden when completed) */}
          {!isCompleted && !oppAVAccessGranted && myAVAccessToOpp !== "pending" && myAVAccessToOpp !== "denied" && (
            <button data-testid="request-av-access" onClick={() => requestAVAccess(myTeamSide)} style={{ width: '100%', padding: '14px', background: 'rgba(255,152,0,0.1)', border: '2px solid rgba(255,152,0,0.3)', borderRadius: 10, color: '#ff9800', fontSize: 14, fontWeight: 700, cursor: 'pointer', marginTop: 20, marginBottom: 16, fontFamily: "'Outfit', sans-serif" }}>
              Request AV Access to {oppTeamName}
            </button>
          )}
          {!isCompleted && myAVAccessToOpp === "pending" && <div style={{ padding: 14, background: 'rgba(33,150,243,0.08)', border: '1px solid rgba(33,150,243,0.2)', borderRadius: 10, marginTop: 20, marginBottom: 16, textAlign: 'center' }}><p style={{ color: '#2196f3', fontSize: 14 }}>AV access request pending...</p></div>}
          {!isCompleted && myAVAccessToOpp === "denied" && <div style={{ padding: 14, background: 'rgba(244,67,54,0.08)', border: '1px solid rgba(244,67,54,0.2)', borderRadius: 10, marginTop: 20, marginBottom: 16, textAlign: 'center' }}><p style={{ color: '#f44336', fontSize: 14 }}>AV access denied by {oppTeamName}</p></div>}

          <TeamHeader name={oppTeamName} score={isCompleted ? (oppSide === "home" ? homeScoreFinal : awayScoreFinal) : null} />
          {oppPlayers?.map(p => <OpponentCard key={p.id} player={p} verifications={oppSide === "home" ? matchup.home_verifications : matchup.away_verifications} playCounts={oppSide === "home" ? matchup.home_play_counts : matchup.away_play_counts} canVerify={oppAVAccessGranted} readOnly={isCompleted} onVerify={verifyPlayer} onPlayCount={updatePlayCount} />)}
        </>
      )}

      {/* Admin view */}
      {isAdmin && (
        <>
          <TeamHeader name={schedule.home_team_name} score={isCompleted ? homeScoreFinal : null} onRefresh={loadData} />
          {home_players.map(p => <OpponentCard key={p.id} player={p} verifications={matchup.home_verifications} playCounts={matchup.home_play_counts} canVerify readOnly={isCompleted} onVerify={verifyPlayer} onPlayCount={updatePlayCount} />)}
          <div style={{ marginTop: 24 }} />
          <TeamHeader name={schedule.away_team_name} score={isCompleted ? awayScoreFinal : null} />
          {away_players.map(p => <OpponentCard key={p.id} player={p} verifications={matchup.away_verifications} playCounts={matchup.away_play_counts} canVerify readOnly={isCompleted} onVerify={verifyPlayer} onPlayCount={updatePlayCount} />)}
        </>
      )}

      {/* Complete Game button — only home team admin (or higher) while in-progress */}
      {canComplete && (
        <button data-testid="complete-game-btn" onClick={() => setShowCompleteModal(true)} style={{
          width: '100%', marginTop: 28, padding: '16px', background: 'var(--cyan)', border: 'none',
          borderRadius: 12, color: '#000', fontSize: 15, fontWeight: 800, cursor: 'pointer',
          fontFamily: "'Outfit', sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          <Flag size={18} /> Complete Game &amp; Enter Final Score
        </button>
      )}

      {/* Complete Game Modal */}
      {showCompleteModal && (
        <div data-testid="complete-game-modal" onClick={() => setShowCompleteModal(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#1a2230', border: '1px solid #253040', borderRadius: 16,
            padding: 28, width: '100%', maxWidth: 420,
          }}>
            <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, fontFamily: "'Outfit', sans-serif" }}>Enter Final Score</h3>
            <p style={{ color: '#888', fontSize: 13, marginBottom: 22 }}>{schedule.home_team_name} vs {schedule.away_team_name}</p>

            <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#888', marginBottom: 6, fontWeight: 600 }}>{schedule.home_team_name}</label>
                <input data-testid="home-score-input" type="number" min="0" value={homeScore} onChange={e => setHomeScore(e.target.value)} placeholder="0" style={{
                  width: '100%', padding: '12px 14px', background: '#0f141d', border: '1px solid #253040',
                  borderRadius: 10, color: '#fff', fontSize: 22, fontWeight: 700, textAlign: 'center',
                  fontFamily: "'Outfit', sans-serif",
                }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#888', marginBottom: 6, fontWeight: 600 }}>{schedule.away_team_name}</label>
                <input data-testid="away-score-input" type="number" min="0" value={awayScore} onChange={e => setAwayScore(e.target.value)} placeholder="0" style={{
                  width: '100%', padding: '12px 14px', background: '#0f141d', border: '1px solid #253040',
                  borderRadius: 10, color: '#fff', fontSize: 22, fontWeight: 700, textAlign: 'center',
                  fontFamily: "'Outfit', sans-serif",
                }} />
              </div>
            </div>

            {completeError && <p style={{ color: '#f44336', fontSize: 13, marginBottom: 14 }}>{completeError}</p>}

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowCompleteModal(false)} style={{
                flex: 1, padding: '12px', background: 'transparent', border: '1px solid #253040',
                borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}>Cancel</button>
              <button data-testid="confirm-complete-game" disabled={completing} onClick={completeGame} style={{
                flex: 1, padding: '12px', background: completing ? '#555' : '#4caf50', border: 'none',
                borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 700, cursor: completing ? 'not-allowed' : 'pointer',
              }}>{completing ? 'Saving...' : 'Complete Game'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TeamHeader = ({ name, isMyTeam, score, onRefresh }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12, marginTop: 8 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <h3 style={{ fontSize: 17, fontWeight: 700, color: isMyTeam ? 'var(--cyan)' : '#ff6b00' }}>{name}</h3>
      {isMyTeam && <span style={{ fontSize: 11, color: '#888', background: '#1a1e28', padding: '2px 8px', borderRadius: 6 }}>Your Team</span>}
      {score !== null && score !== undefined && (
        <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--cyan)', marginLeft: 6, fontFamily: "'Outfit', sans-serif" }}>({score})</span>
      )}
    </div>
    {onRefresh && (
      <button data-testid={`refresh-team-${name.replace(/\s+/g,'-').toLowerCase()}`} onClick={onRefresh} title="Refresh counts" style={{
        width: 36, height: 36, borderRadius: '50%', background: '#253040', border: 'none',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <RefreshCw size={16} style={{ color: 'var(--cyan)' }} />
      </button>
    )}
  </div>
);

/* ─── Own Team Card: shows play count (no refresh per-player anymore) ─── */
const OwnTeamCard = ({ player, playCounts }) => {
  const pCount = (playCounts || {})[player.id] || 0;
  const avDone = player.av_status === "verified";

  return (
    <div data-testid={`own-player-${player.id}`} style={{
      background: '#1a2230', border: '1px solid #253040', borderRadius: 14, padding: '18px 20px', marginBottom: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--cyan)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: '#000' }}>
            {player.name?.charAt(0)}
          </div>
          <div>
            <p style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>{player.name}</p>
            <p style={{ fontSize: 14, color: '#888' }}>#{player.jersey_number || '?'}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
              {avDone ? <Check size={14} style={{ color: '#4caf50' }} /> : <AlertTriangle size={14} style={{ color: '#ff9800' }} />}
              <span style={{ fontSize: 13, color: avDone ? '#4caf50' : '#ff9800', fontWeight: 600 }}>
                {avDone ? 'AV Complete' : 'AV Needed'}
              </span>
            </div>
          </div>
        </div>
        <div style={{ width: 42, height: 42, borderRadius: '50%', background: avDone ? '#4caf50' : 'transparent', border: avDone ? 'none' : '2px solid #555', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {avDone && <Check size={20} style={{ color: '#fff' }} />}
        </div>
      </div>

      <div style={{ height: 1, background: '#253040', marginBottom: 14 }} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 15, color: '#888' }}>Play Count:</span>
        <span style={{ fontSize: 28, fontWeight: 800, color: '#fff', fontFamily: "'Outfit', sans-serif", minWidth: 32, textAlign: 'center' }}>{pCount}</span>
      </div>
    </div>
  );
};

/* ─── Opponent Card: verify -> then play count +/- (or read-only) ─── */
const OpponentCard = ({ player, verifications, playCounts, canVerify, readOnly, onVerify, onPlayCount }) => {
  const vStatus = (verifications || {})[player.id];
  const pCount = (playCounts || {})[player.id] || 0;
  const avDone = player.av_status === "verified";
  const isVerified = vStatus === "verified";

  return (
    <div data-testid={`opp-player-${player.id}`} style={{
      background: '#1a2230', border: '1px solid #253040', borderRadius: 14, padding: '18px 20px', marginBottom: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--cyan)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: '#000' }}>
            {player.name?.charAt(0)}
          </div>
          <div>
            <p style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>{player.name}</p>
            <p style={{ fontSize: 14, color: '#888' }}>#{player.jersey_number || '?'}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
              {avDone ? <Check size={14} style={{ color: '#4caf50' }} /> : <AlertTriangle size={14} style={{ color: '#ff9800' }} />}
              <span style={{ fontSize: 13, color: avDone ? '#4caf50' : '#ff9800', fontWeight: 600 }}>
                {avDone ? 'AV Complete' : 'AV Needed'}
              </span>
            </div>
          </div>
        </div>
        <div style={{
          width: 42, height: 42, borderRadius: '50%',
          background: vStatus === 'verified' ? '#4caf50' : vStatus === 'yield' ? '#ff9800' : vStatus === 'denied' ? '#f44336' : 'transparent',
          border: vStatus ? 'none' : '2px solid #555',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {vStatus === 'verified' && <Check size={20} style={{ color: '#fff' }} />}
          {vStatus === 'yield' && <AlertTriangle size={18} style={{ color: '#fff' }} />}
          {vStatus === 'denied' && <X size={20} style={{ color: '#fff' }} />}
        </div>
      </div>

      {canVerify && avDone && player.av_data && (
        <>
          <div style={{ height: 1, background: '#253040', marginBottom: 14 }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <button data-testid={`view-id-${player.id}`} style={{
              display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
              color: 'var(--cyan)', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}>
              <CreditCard size={16} /> View ID
            </button>
            <span style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>
              {player.av_data?.player_dob ? new Date(player.av_data.player_dob + 'T00:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : 'N/A'}
            </span>
            <button data-testid={`view-bc-${player.id}`} style={{
              display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
              color: 'var(--cyan)', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}>
              <FileText size={16} /> View B.C.
            </button>
          </div>
        </>
      )}

      {/* Verify buttons — hidden in read-only/completed mode */}
      {canVerify && !isVerified && !readOnly && (
        <>
          <div style={{ height: 1, background: '#253040', marginBottom: 14 }} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button data-testid={`verify-green-${player.id}`} onClick={() => onVerify(player.id, "verified")} style={{
              width: 48, height: 48, borderRadius: '50%', background: '#4caf50', border: 'none',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}><Check size={22} style={{ color: '#fff' }} /></button>
            <button data-testid={`verify-yellow-${player.id}`} onClick={() => onVerify(player.id, "yield")} style={{
              width: 48, height: 48, borderRadius: '50%', background: '#ffc107', border: 'none',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}><AlertTriangle size={20} style={{ color: '#fff' }} /></button>
            <button data-testid={`verify-red-${player.id}`} onClick={() => onVerify(player.id, "denied")} style={{
              width: 48, height: 48, borderRadius: '50%', background: '#f44336', border: 'none',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}><X size={22} style={{ color: '#fff' }} /></button>
          </div>
        </>
      )}

      {/* Play Count: +/- if active & verified, read-only total if completed */}
      {(isVerified || (readOnly && vStatus)) && (
        <>
          <div style={{ height: 1, background: '#253040', marginBottom: 14, marginTop: 14 }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 15, color: '#888' }}>Play Count{readOnly ? ' Total' : ''}:</span>
            {readOnly ? (
              <span style={{ fontSize: 28, fontWeight: 800, color: '#fff', fontFamily: "'Outfit', sans-serif", minWidth: 36, textAlign: 'center' }}>{pCount}</span>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button data-testid={`playcount-minus-${player.id}`} onClick={() => onPlayCount(player.id, pCount - 1)}
                  style={{
                    width: 44, height: 44, borderRadius: '50%', background: '#1a1e28', border: 'none',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}><Minus size={20} style={{ color: '#fff' }} /></button>
                <span style={{ fontSize: 28, fontWeight: 800, color: '#fff', fontFamily: "'Outfit', sans-serif", minWidth: 36, textAlign: 'center' }}>{pCount}</span>
                <button data-testid={`playcount-plus-${player.id}`} onClick={() => onPlayCount(player.id, pCount + 1)}
                  style={{
                    width: 44, height: 44, borderRadius: '50%', background: '#1a1e28', border: 'none',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}><Plus size={20} style={{ color: '#fff' }} /></button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MatchupManager;
